import { ECardinality } from "@kuika/kuika-cl-model";
import type { CascaderOptionType, CascaderValueType } from "antd/es/cascader";
import { getDesignerSymbolPickerOptionName } from "../../../../../shared/utility/designer-symbol-picker-engine";
import {
  compareSymbolPickerPaths,
  matchSymbolPickerPath,
  SEARCH_MATCH_LIMIT
} from "../../../../../shared/utility/symbol-picker-search";

/**
 * The symbol picker's view of the engine's option tree (DesignerSymbolPickerEngine.generateItems,
 * decorated by symbol-picker-option-testids.tsx) — for the columns popup that replaces antd's
 * Cascader behind the `symbolPickerColumnsV2` flag.
 *
 * The options themselves are neither copied nor changed: a pick hands the host the very objects it
 * was given, root → leaf, and `value` = their `value`s — exactly the `(value, selectedOptions)` pair
 * antd's Cascader passed to `onChange`, so `buildSymbolFromSelectedOptions` and every host handler
 * run unchanged (pinned by symbol-picker-popup.golden.test.tsx).
 *
 * Leaf semantics are antd's: an option with no `children`, or an empty `children` array, is a leaf
 * the user can pick; `disabled` on the option or any ancestor makes it unreachable.
 */

export enum ESymbolPickerNodeKind {
  /** Opens a child column. */
  branch = "branch",
  /** Picks. */
  leaf = "leaf",
  /** The "still loading" placeholder the engine appends to Action Results. Never picks. */
  loading = "loading"
}

export interface ISymbolPickerNode {
  /** The index path from the root, `"0.3.1"` — unique, and stable while the tree is (values are
   *  fresh GUIDs on every open, so they are not). */
  key: string;
  /** The (decorated) builder object, handed back untouched. */
  option: CascaderOptionType;
  kind: ESymbolPickerNodeKind;
  /** The option's own name, without the type caption — what search and the preview read. */
  name: string;
  /** "List of Dokuman", "DateTimeOffset" — the caption `generateItemLabel` draws, when there is one. */
  typeCaption?: string;
  /** The action's tag chip on an action-result row. */
  actionTag?: string;
  /** Disabled on the option itself or on an ancestor: shown, never opened or picked. */
  isDisabled: boolean;
  /** Why it is disabled, when the builder said (`title`); never the label mirrored into `title`. */
  disabledReason?: string;
  depth: number;
  parent: ISymbolPickerNode | null;
  children: ISymbolPickerNode[];
}

function hasChildren(option: CascaderOptionType): boolean {
  return Array.isArray(option.children) && option.children.length > 0;
}

function resolveKind(option: CascaderOptionType): ESymbolPickerNodeKind {
  if (hasChildren(option)) {
    return ESymbolPickerNodeKind.branch;
  }
  if (option.loading === true) {
    return ESymbolPickerNodeKind.loading;
  }
  return ESymbolPickerNodeKind.leaf;
}

function readString(value: unknown): string | undefined {
  return typeof value === "string" && value !== "" ? value : undefined;
}

function toNode(
  option: CascaderOptionType,
  parent: ISymbolPickerNode | null,
  index: number,
  depth: number
): ISymbolPickerNode {
  const name: string = getDesignerSymbolPickerOptionName(option);
  const isSelfDisabled: boolean = option.disabled === true;
  const title: string | undefined = readString(option.title);
  const node: ISymbolPickerNode = {
    key: parent ? `${parent.key}.${index}` : String(index),
    option,
    kind: resolveKind(option),
    name,
    typeCaption: readString(option.typeCaption),
    actionTag: readString(option.actionTag),
    isDisabled: isSelfDisabled || parent?.isDisabled === true,
    // The test-id decorator mirrors the label into `title` when the builder gave none; only a title
    // that says something else is a reason.
    disabledReason: isSelfDisabled && title !== undefined && title !== name ? title : parent?.disabledReason,
    depth,
    parent,
    children: []
  };
  const childOptions: CascaderOptionType[] = hasChildren(option) ? (option.children as CascaderOptionType[]) : [];
  node.children = childOptions.map((child: CascaderOptionType, childIndex: number): ISymbolPickerNode =>
    toNode(child, node, childIndex, depth + 1)
  );
  return node;
}

export function toSymbolPickerNodes(options: CascaderOptionType[]): ISymbolPickerNode[] {
  return options.map((option: CascaderOptionType, index: number): ISymbolPickerNode => toNode(option, null, index, 0));
}

export function isSymbolPickerBranch(node: ISymbolPickerNode): boolean {
  return node.kind === ESymbolPickerNodeKind.branch && !node.isDisabled;
}

export function isSymbolPickerPickable(node: ISymbolPickerNode): boolean {
  return node.kind === ESymbolPickerNodeKind.leaf && !node.isDisabled;
}

/** Root-first chain of nodes ending at `node`. */
export function getSymbolPickerNodeChain(node: ISymbolPickerNode): ISymbolPickerNode[] {
  const chain: ISymbolPickerNode[] = [];
  let cursor: ISymbolPickerNode | null = node;
  while (cursor) {
    chain.unshift(cursor);
    cursor = cursor.parent;
  }
  return chain;
}

/** The `selectedOptions` antd's Cascader handed `onChange` for this leaf: the builder objects, root → leaf. */
export function getSymbolPickerSelectedOptions(node: ISymbolPickerNode): CascaderOptionType[] {
  return getSymbolPickerNodeChain(node).map((link: ISymbolPickerNode): CascaderOptionType => link.option);
}

/** The `value` antd's Cascader handed `onChange` alongside: each selected option's `value`. */
export function getSymbolPickerValue(selectedOptions: CascaderOptionType[]): CascaderValueType {
  return selectedOptions.map((option: CascaderOptionType): string | number => option.value as string | number);
}

/** Every leaf (loading placeholders included), in antd's flatten order: depth-first, pre-order. */
export function collectSymbolPickerLeaves(
  nodes: ISymbolPickerNode[],
  into: ISymbolPickerNode[] = []
): ISymbolPickerNode[] {
  for (const node of nodes) {
    if (node.kind === ESymbolPickerNodeKind.branch) {
      collectSymbolPickerLeaves(node.children, into);
    } else {
      into.push(node);
    }
  }
  return into;
}

/** A searchable leaf with its option path prepared once per tree, not once per keystroke. */
export interface ISymbolPickerSearchEntry {
  node: ISymbolPickerNode;
  path: CascaderOptionType[];
}

export function buildSymbolPickerSearchIndex(nodes: ISymbolPickerNode[]): ISymbolPickerSearchEntry[] {
  return collectSymbolPickerLeaves(nodes).map((node: ISymbolPickerNode): ISymbolPickerSearchEntry => ({
    node,
    path: getSymbolPickerSelectedOptions(node)
  }));
}

export interface ISymbolPickerSearchResult {
  /** The listed matches, ranked. */
  matches: ISymbolPickerSearchEntry[];
  /** More paths matched than the list holds. */
  truncated: boolean;
}

/**
 * The Cascader's search, step for step, so the new popup lists exactly the rows the old one did:
 * match paths in flatten order with the picker's own matcher and STOP at `SEARCH_MATCH_LIMIT`, then
 * rank what was collected with `compareSymbolPickerPaths` (antd capped before it sorted — ranking
 * every match and then cutting would list a different set on a large tree).
 */
export function searchSymbolPicker(index: ISymbolPickerSearchEntry[], query: string): ISymbolPickerSearchResult {
  const matches: ISymbolPickerSearchEntry[] = [];
  let truncated: boolean = false;
  for (const entry of index) {
    if (!matchSymbolPickerPath(query, entry.path)) {
      continue;
    }
    if (matches.length >= SEARCH_MATCH_LIMIT) {
      truncated = true;
      break;
    }
    matches.push(entry);
  }
  matches.sort((a: ISymbolPickerSearchEntry, b: ISymbolPickerSearchEntry): number =>
    compareSymbolPickerPaths(a.path, b.path, query)
  );
  return { matches, truncated };
}

/** "Single" / "List" for a node that carries a cardinality but no caption spelling it out. */
export function describeSymbolPickerCardinality(cardinality: unknown): string | undefined {
  if (cardinality === ECardinality.Many) {
    return "List";
  }
  if (cardinality === ECardinality.One) {
    return "Single";
  }
  return undefined;
}

interface INamedTypeLike {
  name?: unknown;
}

/** A type the option names itself — a string, or an `IKType`-like `{ name }`. */
export function readSymbolPickerTypeName(value: unknown): string | undefined {
  if (typeof value === "string") {
    return value === "" ? undefined : value;
  }
  if (value !== null && typeof value === "object") {
    const name: unknown = (value as INamedTypeLike).name;
    return typeof name === "string" && name !== "" ? name : undefined;
  }
  return undefined;
}
